<script setup lang="ts">
import AppButton from '@/components/ui/AppButton.vue'
import AppSelect from '@/components/ui/AppSelect.vue'
import Toggle from '@/components/ui/Toggle.vue'
import { pinia } from '@/stores'
import { useRouterStore } from '@/stores/router'
import { onMounted, ref } from 'vue'
import { useRemoteDiagnose } from '@/stores/remote-diagnose'
import { storeToRefs } from 'pinia'
import { getCustomerAddress } from '@/services/customer'
import Modal from '@/components/ui/Modal.vue'
import { AddressTypes, type Address } from '@/types/remote-diagnose'
import { getStore, removeStore } from '@/utils/remote-diagnose'
import { getCountries } from '@/services/carInfo'
import { getPaymentRoute } from '@/utils/router'
import AddressContent from '@/lang/address.json'
import { useLang } from '@/composables/use-lang'
import { computed } from 'vue'
import { getAvailablePaymentMethods } from '@/services/payment-methods'

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  },
  stripe_key: {
    type: String,
    default: ''
  }
})

const { push } = useRouterStore(pinia)
const remoteDiagnoseStore = useRemoteDiagnose(pinia)
const { changePaymentMethods } = remoteDiagnoseStore
const { state } = storeToRefs(remoteDiagnoseStore)

const langContent = useLang(AddressContent)

const modal = ref<InstanceType<typeof Modal> | null>(null)
const isVisible = ref<boolean>(false)
const useIdeal = ref<boolean>(true)
const countries = ref([])
const addressesConfirmed = ref<boolean>(false)
const checksError = ref<boolean>(false)

// Payment method availability (config-driven). Defaults keep the original
// revolut + iDEAL buttons so we never end up with no payment options.
const showRevolut = ref<boolean>(true)
const showIdeal = ref<boolean>(true)
// When true, the iDEAL slot is routed through Stripe (Stripe replaces iDEAL).
const stripeActive = ref<boolean>(false)

const paymentButtonsGridClass = computed(() =>
  showRevolut.value && showIdeal.value ? 'grid-cols-2' : 'grid-cols-1'
)

const loadPaymentMethods = async () => {
  const config = await getAvailablePaymentMethods(props.aws_url)

  // Stripe still requires a key to route the payment, regardless of the config flag.
  const stripeUsable = !!props.stripe_key

  console.log('loadPaymentMethods config => ', config, 'stripeUsable => ', stripeUsable)

  // Fallback: no response OR everything disabled -> render the original
  // revolut + iDEAL buttons to avoid hiding all methods on a config issue.
  if (!config || (!config.ideal && !config.revolut && !config.stripe)) {
    showRevolut.value = true
    showIdeal.value = true
    stripeActive.value = false
  } else {
    // Stripe only replaces iDEAL when the config enables it AND the key is available.
    stripeActive.value = config.stripe && stripeUsable
    showRevolut.value = config.revolut
    showIdeal.value = config.ideal || stripeActive.value
  }

  // Default the selection to an available method (iDEAL preferred, else revolut).
  useIdeal.value = showIdeal.value
}

const emit = defineEmits(['scroll'])

const onSubmit = () => {
  if (!addressesConfirmed.value || !state.value.terms_and_conditions_accepted) {
    checksError.value = true
    return
  }
  checksError.value = false
  modal.value?.open()
}

const handleNext = () => {
  state.value.has_dongle = true
  state.value.dongle_tested = true
  push('schedule-appointment')
}

const goPayment = () => {
  push(getPaymentRoute(state.value.payment_gateway))
}

const handleIdeal = (value: boolean) => {
  changePaymentMethods(value, stripeActive.value)

  useIdeal.value = value
}

onMounted(async () => {
  emit('scroll')

  // resolve which payment methods are enabled before syncing the gateway
  await loadPaymentMethods()

  // keep the default gateway in sync with the resolved selection / Stripe flag
  changePaymentMethods(useIdeal.value, stripeActive.value)

  if (getStore()) {
    removeStore()
    changePaymentMethods(useIdeal.value, stripeActive.value)
    return null
  }

  if (!state.value.new_client) {
    try {
      const result = await getCustomerAddress(state.value.client_name, props.aws_url)

      if (result.data?.length) {
        const address_shipping = result.data.find(
          (address: Address) => address.address_type === AddressTypes.SHIPPING && !address.disabled
        )
        const address_billing = result.data.find(
          (address: Address) => address.address_type === AddressTypes.BILLING && !address.disabled
        )

        if (address_shipping) {
          address_shipping.postal_code = address_shipping.pincode
          state.value.address_shipping = address_shipping
        }

        if (address_billing) {
          address_billing.postal_code = address_billing.pincode
          state.value.address_billing = address_billing
          isVisible.value = true
        }
      }
    } catch (e) {
      console.log('e => ', e)
    }
  }

  const resCountries = await getCountries(props.aws_url)

  if (resCountries) countries.value = resCountries
})
</script>

<template>
  <form @submit.prevent="onSubmit" class="mx-auto w-full max-w-6xl bg-white">
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-12">
      <section class="lg:col-span-8">
        <div class="rounded-2xl border border-slate-200 bg-white">
          <div class="border-b border-slate-200 p-4 sm:p-5">
            <div class="mb-3 flex items-center justify-between">
              <h3 class="text-sm font-extrabold text-slate-900">{{ langContent.shipping_address_heading }}</h3>
              <span
                class="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                Shipping
              </span>
            </div>

            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-6">
              <input class="input sm:col-span-1 md:col-span-3" type="text"
                :placeholder="langContent.company_placeholder" v-model.trim="state.address_shipping.company_name" />
              <input class="input sm:col-span-1 md:col-span-3" type="text" :placeholder="langContent.name_placeholder"
                v-model.trim="state.client_name" required />

              <input class="input sm:col-span-2 md:col-span-6" type="text"
                :placeholder="langContent.address1_placeholder" v-model.trim="state.address_shipping.address_line1"
                required />
              <input class="input sm:col-span-2 md:col-span-6" type="text"
                :placeholder="langContent.address2_placeholder" v-model.trim="state.address_shipping.address_line2" />

              <input class="input sm:col-span-1 md:col-span-2" type="text" :placeholder="langContent.zip_placeholder"
                v-model.trim="state.address_shipping.postal_code" required />
              <input class="input sm:col-span-1 md:col-span-2" type="text" :placeholder="langContent.city_placeholder"
                v-model.trim="state.address_shipping.city" required />
              <app-select class="md:col-span-2" :label="langContent.country_placeholder"
                v-model="state.address_shipping.country"
                :options="countries.length ? countries : [state.address_shipping.country]" required />

              <input class="input sm:col-span-2 md:col-span-6" type="email" :placeholder="langContent.email_placeholder"
                v-model.trim="state.client_email" required />
            </div>
          </div>

          <div class="flex items-center justify-between gap-3 p-4 sm:p-5">
            <p class="mt-0.5 text-xs text-slate-600">{{ langContent.billing_address_question }}</p>
            <toggle class="shrink-0" :isVisible="isVisible" v-model="isVisible" />
          </div>

          <div v-if="isVisible" class="border-t border-slate-200 p-4 sm:p-5">
            <div class="mb-5 flex items-center justify-between">
              <p class="text-sm font-extrabold text-slate-900">{{ langContent.billing_address_heading }}</p>
              <span
                class="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                Billing
              </span>
            </div>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-6">
              <input class="input sm:col-span-1 md:col-span-3" type="text"
                :placeholder="langContent.company_placeholder" v-model.trim="state.address_billing.company_name" />
              <input class="input sm:col-span-1 md:col-span-3" type="text" :placeholder="langContent.name_placeholder"
                v-model.trim="state.client_name" :required="isVisible" />

              <input class="input sm:col-span-2 md:col-span-6" type="text"
                :placeholder="langContent.address1_placeholder" v-model.trim="state.address_billing.address_line1"
                :required="isVisible" />
              <input class="input sm:col-span-2 md:col-span-6" type="text"
                :placeholder="langContent.address2_placeholder" v-model.trim="state.address_billing.address_line2" />

              <input class="input sm:col-span-1 md:col-span-2" type="text" :placeholder="langContent.zip_placeholder"
                v-model.trim="state.address_billing.postal_code" :required="isVisible" />
              <input class="input sm:col-span-1 md:col-span-2" type="text" :placeholder="langContent.city_placeholder"
                v-model.trim="state.address_billing.city" :required="isVisible" />
              <app-select class="md:col-span-2" :label="langContent.country_placeholder"
                v-model="state.address_billing.country"
                :options="countries.length ? countries : [state.address_billing.country]" :required="isVisible" />
            </div>
          </div>
        </div>
      </section>

      <aside class="lg:col-span-4">


        <div class="my-3 sm:mt-0">
          <p class="text-xs font-semibold text-slate-600">
            {{ langContent.payment_method_question }}
          </p>

          <div class="mt-3 grid gap-3 max-w-sm" :class="paymentButtonsGridClass">
            <button v-if="showRevolut" type="button" @click="handleIdeal(false)" :class="[
              'flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-extrabold transition',
              !useIdeal
                ? 'border-english-vermillion bg-white ring-2 ring-english-vermillion'
                : 'border-slate-200 bg-white hover:border-slate-300'
            ]">
              <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
                <svg class="h-4 w-4 text-slate-700" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="2" />
                  <line x1="2" y1="10" x2="22" y2="10" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <span class="text-slate-900">Card</span>
            </button>

            <button v-if="showIdeal" type="button" @click="handleIdeal(true)" :class="[
              'flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-extrabold transition',
              useIdeal
                ? 'border-english-vermillion bg-white ring-2 ring-english-vermillion'
                : 'border-slate-200 bg-white hover:border-slate-300'
            ]">
              <svg class="h-8 w-8" viewBox="0 -4.54 88.668 88.668" xmlns="http://www.w3.org/2000/svg" fill="black">
                <g id="SVGRepo_bgCarrier" stroke-width="0" />
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                <g id="SVGRepo_iconCarrier">
                  <path fill="none" d="M0 0h88.668v79.598H0z" />
                  <path
                    d="M7.375 7.38v64.877h37.824c24.951 0 35.771-13.973 35.771-32.502 0-18.457-10.82-32.375-35.771-32.375H7.375z"
                    fill="white" />
                  <path
                    d="M11.463 11.472h33.741c22.901 0 31.679 12.506 31.679 28.282 0 18.917-12.258 28.409-31.679 28.409H11.463V11.472zm3.271 3.271v50.151h30.47c18.396 0 28.405-8.572 28.405-25.14 0-17.027-10.85-25.012-28.405-25.012h-30.47z" />
                  <path d="M18.822 43.442h9.754v17.36h-9.754z" />
                  <circle cx="23.698" cy="34.695" r="6.062" />
                  <path
                    d="M49.031 37.531v2.811h-6.95v-11.28h6.728v2.81h-3.918v1.306h3.705v2.811h-3.705v1.543l4.14-.001zm1.216 2.813l3.401-11.286h3.998l3.4 11.286h-2.925l-.638-2.183h-3.676l-.639 2.183h-2.921zm4.384-4.991h2.034l-.933-3.188h-.17l-.931 3.188zm7.828-6.294h2.812v8.473h4.162c-1.142-15.374-13.234-18.696-24.228-18.696H33.488v10.229h1.734c3.162 0 5.125 2.144 5.125 5.594 0 3.56-1.916 5.685-5.125 5.685h-1.734V60.81h11.718c17.866 0 24.088-8.298 24.308-20.468h-7.055V29.059zm-28.973 2.816v5.657h1.736c1.203 0 2.315-.348 2.315-2.874 0-2.467-1.237-2.783-2.315-2.783h-1.736z"
                    fill="#d60172" />
                </g>
              </svg>
              <span class="text-slate-900">iDEAL</span>
            </button>
          </div>
        </div>


        <div class="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <p class="text-sm font-extrabold text-slate-900">{{ langContent.confirm_and_continue }}</p>

          <div class="mt-4 space-y-3">
            <label class="flex items-start gap-3 text-sm text-slate-800">
              <input id="confirm-addresses" type="checkbox" v-model="addressesConfirmed" />
              <span class="leading-snug">{{ langContent.confirm_addresses_label }}</span>
            </label>

            <label class="flex items-start gap-3 text-sm text-slate-800">
              <input id="terms-and-conditions" type="checkbox" v-model="state.terms_and_conditions_accepted" />
              <span class="leading-snug">
                {{ langContent.terms_and_conditions_label }}
                <a class="font-extrabold text-english-vermillion" :href="langContent.terms_and_conditions_link"
                  target="_blank">
                  {{ langContent.terms_and_conditions_link_label }}
                </a>
              </span>
            </label>

            <p class="text-sm font-extrabold text-slate-600">
              {{ langContent.disclaimer }}
            </p>
            <p class="text-sm font-extrabold text-slate-600">
              {{ langContent.disclaimer_shipping_time }}
            </p>
            <p class="text-sm font-extrabold text-slate-600">
              {{ langContent.disclaimer_address_responsibility }}
            </p>

            <p v-if="checksError" class="text-sm font-extrabold text-english-vermillion bg-red-50 p-4 rounded">
              {{ langContent.error_checkboxes_required }}
            </p>
          </div>

          <div class="mt-5">
            <app-button type="submit" class="w-full px-8 py-3 text-base font-extrabold">
              {{ langContent.submit_button }}
            </app-button>
          </div>
        </div>
      </aside>
    </div>

    <modal ref="modal">
      <p>{{ langContent.modal_question }}</p>

      <div class="mt-10 flex justify-evenly">
        <app-button class="w-20" type="button" @click="handleNext">{{ langContent.modal_yes_button }}</app-button>
        <app-button class="w-20" type="button" @click="goPayment">{{ langContent.modal_no_button }}</app-button>
      </div>
    </modal>
  </form>
</template>

<style scoped>
.input,
.select {
  @apply w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-english-vermillion focus:outline-none;
}
</style>
